import { Auth } from "aws-amplify";

const API_URL = process.env.REACT_APP_API_URL;

interface UserEntry {
  email: string;
  password: string;
  credits: number;
}

interface CreateUsersPayload {
  admin_uuid: string;
  app: string;
  default_password: string;
  users: UserEntry[];
}

export interface ApiResponse {
  ok: boolean;
  status: number;
  message?: string;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const session = await Auth.currentSession();
  return {
    Authorization: session.getIdToken().getJwtToken(),
    "Content-Type": "application/json",
  };
}

export async function createPartnerUsers(
  payload: CreateUsersPayload
): Promise<ApiResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/partner/new-users`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = `Erreur ${response.status}`;
    try {
      const body = await response.json();
      message = body.message || message;
    } catch {}
    return { ok: false, status: response.status, message };
  }

  return { ok: true, status: response.status };
}

// Org tree types matching core's getAllOrgsResp
export interface OrgUser {
  user_id: string;
  email: string;
  role: string;
}

export interface OrgNode {
  organisation_name: string;
  organisation_id: string;
  seats_used: number;
  seats_left: number;
  is_root_org: boolean;
  role: string;
  nb_users_in_org: number;
  users: OrgUser[] | null;
  sub_organisations: OrgNode[] | null;
}

export async function getPartnerOrgs(
  adminUUID: string
): Promise<{ ok: boolean; data?: OrgNode; message?: string }> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/partner/orgs/${adminUUID}`, {
    method: "GET",
    headers,
  });

  if (!response.ok) {
    let message = `Erreur ${response.status}`;
    try {
      const body = await response.json();
      message = body.message || message;
    } catch {}
    return { ok: false, message };
  }

  const body = await response.json();
  return { ok: true, data: body };
}

export async function transferUser(
  fromOrgId: string,
  userId: string,
  toOrgId: string
): Promise<ApiResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${API_URL}/org/${fromOrgId}/transfer-user`, {
    method: "PUT",
    headers,
    body: JSON.stringify({ user_id: userId, to_org_id: toOrgId }),
  });

  if (!response.ok) {
    let message = `Erreur ${response.status}`;
    try {
      const body = await response.json();
      message = body.message || message;
    } catch {}
    return { ok: false, status: response.status, message };
  }

  return { ok: true, status: response.status };
}

// Winelabel credits
export interface UserCredit {
  user_id: string;
  email: string;
  credits_bought: number;
  credits_used: number;
  credits_left: number;
}

export async function getWinelabelCredits(
  adminUUID: string
): Promise<{ ok: boolean; data?: UserCredit[]; message?: string }> {
  const headers = await getAuthHeaders();

  const response = await fetch(
    `${API_URL}/partner/orgs/${adminUUID}/winelabel-credits`,
    { method: "GET", headers }
  );

  if (!response.ok) {
    let message = `Erreur ${response.status}`;
    try {
      const body = await response.json();
      message = body.message || message;
    } catch {}
    return { ok: false, message };
  }

  const body = await response.json();
  return { ok: true, data: body };
}

export async function renewCredits(
  adminUUID: string,
  userId: string,
  creditsBought: number
): Promise<ApiResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(
    `${API_URL}/partner/orgs/${adminUUID}/renew-credits`,
    {
      method: "PUT",
      headers,
      body: JSON.stringify({ user_id: userId, credits_bought: creditsBought }),
    }
  );

  if (!response.ok) {
    let message = `Erreur ${response.status}`;
    try {
      const body = await response.json();
      message = body.message || message;
    } catch {}
    return { ok: false, status: response.status, message };
  }

  return { ok: true, status: response.status };
}
